import { AppData, Lightning, Registry, Router } from '@lightningjs/sdk';
import { LiveEventStream } from 'types/api/media';
import { RoutePath } from 'config/routes';
import { debounceAnnounce } from 'support/generalUtils';
import { translate } from 'support/translate';
import { formatVideoTime, isLiveEvent } from 'support/contentUtils';

import { minutesToMilliseconds } from 'support/timeUtils';
import { navigateHome } from 'support/routerUtils';
import { ErrorCause } from 'components/widgets/ErrorModal';
import AbstractPlaybackPage, {
  AbstractPlaybackPageTemplateSpec,
} from './AbstractPlaybackPage';
import LivePlayerEventHandler from 'components/pages/playback/PlayerEventHandler/LivePlayerEventHandler';

const LIVE_DVR_HEAD_BUFFER = 3;
const LIVE_DVR_SEEK_BUFFER = 0.1;

export default class LivePlaybackPage
  extends AbstractPlaybackPage
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractPlaybackPageTemplateSpec>
{
  private _hasDVR = false;
  private _dvrHist = 0;
  private _liveStream: LiveEventStream | null = null;

  protected static override PlayerEventHandlerType = LivePlayerEventHandler;

  private get dvrHistInSeconds() {
    return this._dvrHist * 60;
  }

  override _handleMediaPlay() {
    if (!this._hasDVR) return true;
    return super._handleMediaPlay();
  }

  override _handleMediaPause() {
    if (!this._hasDVR) return true;
    return super._handleMediaPause();
  }

  override $videoPlayerSeeking(rawTime: number, relativeTime: number) {
    this.updateDvrOverlay(relativeTime);
    super.$videoPlayerSeeking(rawTime, relativeTime);
  }

  override $videoPlayerTimeUpdate(
    relativeTime: number,
    hasReachedCredits: boolean,
  ) {
    if (!this._Player) return;

    const finalTime = this.updateDvrOverlay(relativeTime);

    if (this._shouldAnnounceCurrentTime) {
      this._shouldAnnounceCurrentTime = false;
      this._announceCurrentTime(finalTime);
    }
  }

  override _seek(position: number) {
    const liveSyncPosition = this._Player.getLiveSyncPosition();
    if (!liveSyncPosition) return;

    // if seeking past the live sync position, set to live edge
    if (position > liveSyncPosition) {
      this._Player.seek(liveSyncPosition);
      return;
    }

    // if seeking before the DVR window
    const dvrWindow = liveSyncPosition - this.dvrHistInSeconds;
    if (position < dvrWindow) {
      this._Player.seek(dvrWindow + LIVE_DVR_SEEK_BUFFER);
      return;
    }
    this._Player.seek(position);
  }

  override _play() {
    this._dvrOnResume();
    super._play();
  }

  override _liveButton() {
    this._seek(Infinity);
  }

  override setupPlayer(useBc: boolean) {
    const { mediaContent, hasDrm, drmLicenseUrl, progress } = this.pageData!;

    this._liveStream = mediaContent as LiveEventStream;
    this._Player.liveStreamUrl = this._liveStream.streamUrl;

    // If the event has already ended, show the error modal stop setupPlayer
    if (new Date() >= new Date(this._liveStream.endDateAccess)) {
      this.widgets.errormodal.update({
        cause: ErrorCause.LIVE_ENDED_ON_DEEPLINK,
      });
      AppData?.modalService.open('ErrorModal', { replaceCurrent: true });
      return false;
    }

    this._hasDVR = this._liveStream?.has_dvr;
    this._VideoOverlay.contentType = this._hasDVR ? 'live-dvr' : 'live';
    this._dvrHist = this._liveStream?.hist;

    this._Player.adZone = this._liveStream.adZone;

    this._ayswIdleLimit = minutesToMilliseconds(
      AppData?.ayswIdleTimes?.live ?? 0,
    );
    this._ayswTimeoutFunction = this.fireLiveAyswTimeout.bind(this);

    this._hasReachedEnd = false;

    this._Player.hasDrm = hasDrm;
    this._Player.drmLicenseUrl = drmLicenseUrl;
    this._Player.startTime = progress;

    return true;
  }

  override exitPlayer() {
    this.handlePlayerExit(() => {
      this.cleanupPlayer();
      this._TextTrackDisplay.reset();

      AppData?.device.onPlayerExit();

      // Check if the event has ended
      if (
        this._liveStream &&
        new Date() >= new Date(this._liveStream.endDateAccess)
      ) {
        this.widgets.errormodal.update({
          cause: ErrorCause.LIVE_ENDED_ON_EXIT_PLAYER,
        });
        AppData?.modalService.open('ErrorModal', { replaceCurrent: true });
        return;
      }

      this.navigateAfterExit();
    });
  }

  override async prepareAyswModal() {
    const { mediaContent } = this.pageData!;

    try {
      const liveStream = mediaContent as LiveEventStream;
      if (liveStream) {
        const currentTime = new Date();
        const endTime = new Date(liveStream.endDateStream);

        if (currentTime < endTime) {
          const timeDifference = endTime.getTime() - currentTime.getTime();
          this.clearAyswEventTimeout();
          this._ayswLiveEventEndedTimeout = Registry.setTimeout(
            this.setupAyswEventEnded.bind(this),
            timeDifference,
          );
        } else {
          this.setupAyswEventEnded();
        }
      } else {
        this.setupAyswEventEnded();
      }
    } catch (e) {
      console.error(e);
      this.setupAyswEventEnded();
    }
  }

  override getAyswLabel(): { showName: string; cancelLabel: string } {
    const { mediaContent } = this.pageData!;

    let showName = '';
    let cancelLabel = '';
    if (isLiveEvent(mediaContent)) {
      showName = (mediaContent as LiveEventStream).title;
      cancelLabel = translate('global.returnToEvent');
    }

    return { showName, cancelLabel };
  }

  override _handleContinueOnAysw() {
    this.resetAyswTimer();
    this._Player.resume();
  }

  override _handleBackOnAysw() {
    if (!this.pageData) return;
    const { mediaContent } = this.pageData;

    const liveEvent = mediaContent as LiveEventStream;
    Router.navigate(`${RoutePath.LIVE_EVENT_DETAIL}/${liveEvent.slug}`);
  }

  override _announceCurrentTime(currentTime: number) {
    this.fireAncestors('$announcerCancel');

    debounceAnnounce(
      this,
      translate(
        currentTime <= LIVE_DVR_HEAD_BUFFER
          ? 'ttsPrompts.playingLive'
          : 'ttsPrompts.playingBehindLive',
        formatVideoTime(currentTime),
      ),
    );
  }

  private setupAyswEventEnded() {
    this.widgets.ayswmodal.update({
      title: translate('playback.eventEnded'),
      confirmLabel: '',
      cancelLabel: translate('global.returnToHome'),
      onConfirm: null,
      onCancel: navigateHome.bind(this),
      onBack: navigateHome.bind(this),
    });
  }

  private clearAyswEventTimeout() {
    if (this._ayswLiveEventEndedTimeout)
      Registry.clearTimeout(this._ayswLiveEventEndedTimeout);
    this._ayswLiveEventEndedTimeout = null;
  }

  private fireLiveAyswTimeout() {
    this.showAyswOrEventEnded();
  }

  private _isPlayingLive(rawTime: number): boolean {
    const liveSyncPosition = this._Player.getLiveSyncPosition();
    if (!liveSyncPosition) return false;

    const secondsBehindLiveEdge = Math.max(0, liveSyncPosition - rawTime);
    return secondsBehindLiveEdge <= this._Player.getTargetLatency();
  }

  private _dvrOnResume() {
    if (this._hasDVR) {
      const relativeTime = this._Player.getRelativeTime();
      const liveSyncPosition = this._Player.getLiveSyncPosition();

      this._VideoOverlay.isPlayingLive = this._isPlayingLive(relativeTime);

      // if paused point is before the current DVR window, seek to start of window
      const dvrWindowStart = liveSyncPosition! - this.dvrHistInSeconds;

      if (relativeTime < dvrWindowStart) {
        this._Player.seek(dvrWindowStart + LIVE_DVR_SEEK_BUFFER);
      }
    }
  }

  private updateDvrOverlay(relativeTime: number) {
    const liveSyncPosition = this._Player.getLiveSyncPosition();
    if (liveSyncPosition) {
      const behindLiveEdge = Math.max(0, liveSyncPosition - relativeTime);

      // Remains 0 while the window is not full. Then increments every second.
      const windowsStartTime = Math.max(
        liveSyncPosition - this.dvrHistInSeconds,
        0,
      );

      // Starts 0, then maxes at total hist size in seconds.
      const totalWindowSize = Math.max(0, liveSyncPosition - windowsStartTime);

      this._VideoOverlay.dvrWindowSize = totalWindowSize;
      this._VideoOverlay.liveSyncPosition = liveSyncPosition;
      const finalTime = Math.min(behindLiveEdge, totalWindowSize);
      this._VideoOverlay.updateProgress(finalTime);

      const isPlayingStartOfHist =
        relativeTime <= LIVE_DVR_HEAD_BUFFER ||
        (totalWindowSize > 0 &&
          Math.floor(behindLiveEdge) >= Math.floor(totalWindowSize));

      this._VideoOverlay.isPlayingStartOfHist = isPlayingStartOfHist;
      this._VideoOverlay.isPlayingLive = this._isPlayingLive(relativeTime);

      return finalTime;
    }
    return 0;
  }
}
